import { Role } from "types/enums";
import {
  UserCircleIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  // CodeBracketIcon,
  ArchiveBoxArrowDownIcon,
  FaceSmileIcon,
  ChatBubbleBottomCenterTextIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline";
import { UppdragStatus } from "types/uppdrag.type";
import { Company } from "types/company.type";
import { type LinkProps } from "@tanstack/react-router";

export enum RouteUrl {
  Home = "/",
  Login = "/signin",
  Signup = "/signup",
  App = "/app",
  Customers = "kunder",
  Dashboard = "oversikt",
  Uppdrag = "uppdrag",
  Organization = "organisation",
  Account = "konto",
  WasteStations = "mottagsstation",
  UserSchedulePage = "userschema",
  FiveYearsInspection = "fivey",
  SixMonthsInspection = "sixm",
  Waste = "tomning",
  ForgotPassword = "/forgot-password",
  ResetPassword = "/reset-password",
  MagicLinkLogin = "/magiclink",
  Changelog = "uppdateringar",
  OpenTransportSedelList = "transport-sedlar",
  VehicleContentQr = "vehicle-content-qr",
  VehicleContent = "vehicle-content",
  Feedback = "feedback",
  Avfallsregistret = "avfallsregistret",
  RapporteraTransport = "rapportera-transport",
  OmbudsRapportering = "rapportera-som-ombud",
  Companies = "foretag",
  WasteStationNotification = "avisering-om-avfall",
  ServiceSchedule = "service-schedule",
}

export enum RouteLabels {
  Dashboard = "Dashboard",
  Customers = "Kunder",
  Uppdrag = "Uppdrag",
  WasteStations = "Mottagarstationer",
  Organization = "Organisation",
  Account = "Konto",
  TransportDocuments = "Transportdokument",
  UserSchedule = "Mina uppdrag",
  Changelog = "Uppdateringar",
  OpenTransportDocuments = "Last",
  Feedback = "Återkoppling",
  Avfallsregistret = "Avfall",
  RapporteraTransport = "Rapportera transport",
  RapporteraTransportPlanering = "Rapportera som ombud",
  Companies = "Företag - Admin",
  ServiceSchedule = "Planering",
}

export type RouteDefinition = {
  label: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  minRole?: Role;
  exactRole?: Role;
  bottom?: boolean;
} & Pick<LinkProps, "to" | "search">;

const routeDefinitions: RouteDefinition[] = [
  {
    to: "/app/foretag",
    label: RouteLabels.Companies,
    icon: CurrencyDollarIcon,
    minRole: Role.SuperUser,
  },
  {
    to: "/app/uppdrag",
    search: {
      statuses: [UppdragStatus.Scheduled, UppdragStatus.Started],
      direction: "DESC",
      page: 1,
      sortby: "scheduleDate",
    },
    label: RouteLabels.Uppdrag,
    icon: CalendarDaysIcon,
    minRole: Role.Operator,
  },
  {
    to: "/app/kunder",
    label: RouteLabels.Customers,
    icon: FaceSmileIcon,
    minRole: Role.Operator,
  },
  {
    to: "/app/mottagsstation",
    label: RouteLabels.WasteStations,
    icon: ArchiveBoxArrowDownIcon,
    minRole: Role.Admin,
  },
  {
    to: "/app/avfallsregistret",
    label: RouteLabels.Avfallsregistret,
    icon: ArchiveBoxArrowDownIcon,
    minRole: Role.Admin,
  },
  {
    to: "/app/service-schedule",
    label: RouteLabels.ServiceSchedule,
    icon: CalendarDaysIcon,
    minRole: Role.Admin,
  },
  {
    to: "/app/organisation",
    label: RouteLabels.Organization,
    icon: UserGroupIcon,
    minRole: Role.Admin,
  },
  {
    to: "/app/konto",
    label: RouteLabels.Account,
    icon: UserCircleIcon,
    minRole: Role.Operator,
  },
  {
    to: "/app/feedback",
    label: RouteLabels.Feedback,
    icon: ChatBubbleBottomCenterTextIcon,
    minRole: Role.Operator,
    bottom: true,
  },
];

export interface AppRoute extends Pick<LinkProps, "to" | "search"> {
  isCurrent: boolean;
  label: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  bottom?: boolean;
  ping?: boolean;
  errorPing?: boolean;
}

export const getAppRoutes = (
  role: Role,
  currentPath: string,
  company?: Company,
  showAccountPagePing?: boolean,
  showWasteError?: boolean,
): AppRoute[] => {
  return routeDefinitions
    .filter(({ minRole, exactRole }) =>
      !!exactRole ? exactRole === role : !minRole ? false : minRole <= role,
    )
    .filter(
      ({ label }) =>
        label !== RouteLabels.Avfallsregistret ||
        !!company?.settings?.isNvActive,
    )
    .filter(
      ({ label }) =>
        label !== RouteLabels.Customers ||
        role > Role.Operator ||
        company?.operatorPrivileges.viewCustomerList,
    )
    .map((route) => ({
      ...route,
      isCurrent: currentPath.toLowerCase().startsWith(route.to!),
      ping: showAccountPagePing && route.to === "/app/konto",
      errorPing: showWasteError && route.to === "/app/avfallsregistret",
    }));
};
